import { useId, type InputHTMLAttributes, type ReactNode } from 'react'
import { cn } from '../cn.js'

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string
  /** Shown below the field. Replaced by `error` when present. */
  hint?: ReactNode
  error?: string | undefined
}

export function TextField({
  label,
  hint,
  error,
  className,
  required,
  ...rest
}: TextFieldProps) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
        {required && (
          <span className="text-danger" aria-hidden="true">
            {' '}
            *
          </span>
        )}
        {required && <span className="sr-only"> (required)</span>}
      </label>

      <input
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        // Point at whichever description is actually rendered, so screen
        // readers announce the error rather than the stale hint.
        aria-describedby={error ? errorId : hint ? hintId : undefined}
        className={cn(
          'min-h-[44px] rounded-lg border bg-white px-3 py-2 text-ink',
          'placeholder:text-muted-soft',
          // line-strong, not line: an input outline is a UI component boundary
          // and needs 3:1 (WCAG 2.2 1.4.11). The decorative token is 1.44:1.
          error ? 'border-danger' : 'border-line-strong',
          'disabled:bg-surface-alt disabled:text-muted',
          className,
        )}
        {...rest}
      />

      {error ? (
        // Never colour alone: the icon and the word carry the meaning too, for
        // colour-blind users and for anyone on a poor screen (§9.3).
        <p id={errorId} role="alert" className="flex items-start gap-1.5 text-sm text-danger">
          <span aria-hidden="true">⚠</span>
          <span>{error}</span>
        </p>
      ) : hint ? (
        <p id={hintId} className="text-sm text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  )
}
